import { z } from 'zod';
import { jsonObject, timestamp, uuid } from './common.js';
/**
 * Cross-cutting (§4, §24). `cache_entries` (the only cache, E1),
 * `ws_notifications_outbox` (fan-out bridge, E17), `audit_events` (append-only
 * audit + activity store).
 */
export const cacheEntrySchema = z.object({
    namespace: z.string().min(1), // PK part 1
    key: z.string().min(1), // PK part 2
    valueJsonb: z.unknown(),
    tags: z.array(z.string()), // GIN-indexed, for bulk invalidateByTag
    expiresAt: timestamp, // expired rows read as a miss (never serve stale)
    createdAt: timestamp,
});
export const wsOutboxSchema = z.object({
    id: z.string(), // bigserial/uuid — the NOTIFY payload is this id only
    channel: z.string().min(1),
    payloadJsonb: z.unknown(),
    createdAt: timestamp,
});
/**
 * Closed audit action set (§24). ONE writer `audit(event)` in @xdocs/core, in
 * the SAME transaction as the mutation.
 */
export const auditAction = z.enum([
    // staff / admin
    'user.tier_change',
    'user.credit_adjust',
    'user.role_change',
    'user.suspend',
    'user.force_logout',
    'user.resend_verify',
    'promo.create',
    'promo.update',
    'promo.deactivate',
    'asset.unpublish',
    'asset.remove',
    'comment.remove',
    'landing.publish',
    'legal.publish',
    'contact.handle',
    'impersonation.start',
    'impersonation.end',
    // user-side
    'document.create',
    'share.publish',
    'comment.create',
    // Danger Zone (F10 self-service) — self-service account lifecycle actions.
    'user.delete_account',
    'user.export_data',
]);
export const auditEventSchema = z.object({
    id: uuid,
    actorUserId: uuid,
    /** Set when the action occurred inside an impersonation session (§15.1). */
    impersonatedUserId: uuid.nullable(),
    action: auditAction,
    targetType: z.string().nullable(),
    targetId: z.string().nullable(),
    ip: z.string().nullable(),
    userAgent: z.string().nullable(),
    /** Before/after + reason; changed fields only; NO secrets/PII. */
    metadataJsonb: jsonObject.nullable(),
    createdAt: timestamp,
});
